import { useState } from "react";
import { Plus, X } from "lucide-react";
import { ApiError, errorMessage, resolveReferenceVideo } from "@/lib/api";
import { Button } from "@/components/ui/Button";

/**
 * "Add a video" — the coach-side field for attaching a reference video
 * (issue #675).
 *
 * **Collapsed when empty**, by explicit design decision: most notes will
 * never carry a reference, and a permanent field pushes Save further down a
 * form coaches fill in constantly — worse on a phone. With nothing attached
 * this is a single `+ Add a video` line; clearing collapses back to it, so a
 * note without a video never pays for the field.
 *
 * The coach pastes a URL and the server resolves it. The preview matters:
 * without it a coach can't tell a typo'd or private link from a good one
 * until a player reports it broken. What's stored is what the server
 * returned — the raw URL is discarded, so the value that eventually reaches
 * an `iframe src` is one the server constructed, never one the coach typed.
 *
 * Generic over the form shape so the note composer and the goal composer
 * can share it; both carry the same five `ref_video_*` string fields.
 */

export type ReferenceVideoFieldValues = {
  ref_video_provider: "" | "youtube" | "vimeo" | "file";
  ref_video_id: string;
  ref_video_label: string;
  ref_video_title: string;
  ref_video_thumb: string;
};

type Props = {
  values: ReferenceVideoFieldValues;
  /** Patch one or more fields on the parent form. */
  onChange: (patch: Partial<ReferenceVideoFieldValues>) => void;
  disabled?: boolean;
};

const PLACEHOLDER =
  "youtube.com/watch?v=… · vimeo.com/… · or a video file link";

export function ReferenceVideoField({ values, onChange, disabled }: Props) {
  const attached = !!values.ref_video_provider && !!values.ref_video_id;
  // Expanded when a video is attached, or when the coach opened the field
  // manually and hasn't pasted anything yet.
  //
  // `expandedManually` tracks ONLY the second case, so that `open` stays a
  // derived value. A `useState(attached)` snapshot would go stale: the note
  // composer is reset imperatively via `composerRef.reset()` rather than
  // remounted (CoachReview keeps sticky fields across a save), so the values
  // prop can drop to empty with this component still mounted — leaving the
  // panel open on an empty URL input, which is what the collapse-when-empty
  // decision in #675 exists to avoid.
  const [expandedManually, setExpandedManually] = useState(false);
  const open = attached || expandedManually;
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const clear = () => {
    onChange({
      ref_video_provider: "",
      ref_video_id: "",
      ref_video_label: "",
      ref_video_title: "",
      ref_video_thumb: "",
    });
    setUrl("");
    setError(null);
    setExpandedManually(false);
  };

  const resolve = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Paste a video link first");
      return;
    }
    setResolving(true);
    setError(null);
    try {
      const { reference_video: rv } = await resolveReferenceVideo(trimmed);
      onChange({
        ref_video_provider: rv.provider,
        ref_video_id: rv.video_id,
        ref_video_title: rv.title,
        ref_video_thumb: rv.thumb ?? "",
      });
    } catch (err) {
      // The backend's 400 detail is written for the coach ("TikTok links
      // aren't supported…", "That video is private, deleted, or can't be
      // embedded") — surface it rather than a generic failure.
      setError(
        err instanceof ApiError && err.status === 400
          ? errorMessage(err)
          : "Couldn't check that link. Try again.",
      );
    } finally {
      setResolving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setExpandedManually(true)}
        disabled={disabled}
        data-testid="composer-add-reference-video"
        className="flex w-fit items-center gap-1.5 rounded-control px-1 py-1 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
      >
        <Plus aria-hidden className="h-3.5 w-3.5" />
        Add a video
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-inset bg-elevated p-3"
      data-testid="composer-reference-video"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
          Watch this
        </span>
        <div className="flex items-center gap-1">
          {/* Swapping one video for another used to mean ✕ (which collapsed
            * the whole field and discarded the "why this video" text) then
            * reopening and re-pasting. Replace keeps the field open and
            * keeps the label. */}
          {attached ? (
            <button
              type="button"
              onClick={() => {
                onChange({
                  ref_video_provider: "",
                  ref_video_id: "",
                  ref_video_title: "",
                  ref_video_thumb: "",
                });
                setUrl("");
                setError(null);
                setExpandedManually(true);
              }}
              data-testid="composer-reference-video-replace"
              className="rounded-control px-1 py-0.5 text-xs text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Replace
            </button>
          ) : null}
          <button
            type="button"
            onClick={clear}
            data-testid="composer-reference-video-clear"
            aria-label="Remove video"
            className="rounded-control p-0.5 text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Thumbnail beside text, stacking on a narrow phone (#675: the preview
        * row keeps side-by-side down to ~340px, then stacks). */}
      {attached ? (
        <div className="flex flex-col gap-2 min-[340px]:flex-row min-[340px]:items-start min-[340px]:gap-3">
          {values.ref_video_thumb ? (
            // Our own copy, served by us — see referenceVideo.ts.
            <img
              src={`/api/coach/reference-video/thumb/${encodeURIComponent(
                values.ref_video_thumb,
              )}`}
              alt=""
              data-testid="composer-reference-video-thumb"
              className="h-[61px] w-[108px] shrink-0 rounded-inset object-cover"
            />
          ) : (
            <span className="flex h-[61px] w-[108px] shrink-0 items-center justify-center rounded-inset bg-canvas text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              {values.ref_video_provider === "file" ? "File" : "No preview"}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">
              {values.ref_video_title || values.ref_video_id}
            </p>
            <p className="text-xs text-ink-muted">
              {values.ref_video_provider === "youtube"
                ? "YouTube"
                : values.ref_video_provider === "vimeo"
                  ? "Vimeo"
                  : "Video file"}
            </p>
            {values.ref_video_provider === "file" ? (
              // Worth saying plainly: a club-hosted file involves nobody
              // else, and a coach choosing between sources should know.
              <p className="mt-1 text-xs text-good">
                Plays in tktic&rsquo;s own player — no third party
              </p>
            ) : (
              <p className="mt-1 text-xs text-good">Thumbnail saved to tktic</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                // Enter inside a composer field must not submit the whole
                // note — resolve instead.
                if (e.key === "Enter") {
                  e.preventDefault();
                  void resolve();
                }
              }}
              placeholder={PLACEHOLDER}
              disabled={disabled || resolving}
              data-testid="composer-reference-video-url"
              aria-label="Video link"
              className="min-w-0 flex-1 rounded-control border border-rule bg-canvas px-2 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void resolve()}
              disabled={disabled || resolving}
              data-testid="composer-reference-video-check"
            >
              {resolving ? "Checking…" : "Check link"}
            </Button>
          </div>
          {error ? (
            <p
              data-testid="composer-reference-video-error"
              className="text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      )}

      {attached ? (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Why this video <span className="lowercase">optional</span>
          </span>
          <input
            value={values.ref_video_label}
            onChange={(e) => onChange({ ref_video_label: e.target.value })}
            disabled={disabled}
            maxLength={200}
            placeholder="Watch his body shape before the pass"
            data-testid="composer-reference-video-label"
            className="rounded-control border border-rule bg-canvas px-2 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
      ) : null}
    </div>
  );
}
